import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  TenantApiError,
  type UpdateTenantBusinessBrandingInput,
} from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type AccentPreset = UpdateTenantBusinessBrandingInput["accent_preset"];
type ThemeMode = UpdateTenantBusinessBrandingInput["theme_mode"];
type FontPreset = UpdateTenantBusinessBrandingInput["font_preset"];
type RadiusPreset = UpdateTenantBusinessBrandingInput["radius_preset"];

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready" };

// Preset ids only — never a hex value. swatchClass names a small app-local
// CSS class (styles.css) that renders one representative preview color; the
// actual public-menu color mapping is Slice 4D's concern, and this preview
// is not that mapping, just a way to make five plain radio labels
// recognizable at a glance.
const ACCENT_OPTIONS: Array<{
  value: AccentPreset;
  label: string;
  swatchClass: string;
}> = [
  { value: "terracotta", label: "Terracotta", swatchClass: "qr-swatch-terracotta" },
  { value: "evergreen", label: "Koyu yeşil", swatchClass: "qr-swatch-evergreen" },
  { value: "navy", label: "Lacivert", swatchClass: "qr-swatch-navy" },
  { value: "plum", label: "Mürdüm", swatchClass: "qr-swatch-plum" },
  { value: "amber", label: "Kehribar", swatchClass: "qr-swatch-amber" },
];

const THEME_MODE_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: "system", label: "Sistem ayarı" },
  { value: "light", label: "Açık" },
  { value: "dark", label: "Koyu" },
];

const FONT_PRESET_OPTIONS: Array<{ value: FontPreset; label: string }> = [
  { value: "sans", label: "Sade" },
  { value: "serif", label: "Klasik" },
];

const RADIUS_PRESET_OPTIONS: Array<{ value: RadiusPreset; label: string }> = [
  { value: "compact", label: "Keskin" },
  { value: "default", label: "Dengeli" },
  { value: "rounded", label: "Yuvarlak" },
];

// TenantApiError.message carries the backend's curated public error_message,
// never a raw cause or response body — same helper as Account/Team.
function describe(err: unknown, fallback: string): string {
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}

// No client-side role gating: an editor can open this page and see the
// current values (GET /businesses/current already permits owner/admin/
// editor), but Kaydet on an editor account gets the same backend 403 as any
// other role-restricted mutation in this app, surfaced through the same
// generic describe() path used everywhere else — see Team.tsx's row
// mutations and Account.tsx's password change for the same pattern. No
// tenant-panel route today hides or disables its own UI based on the
// caller's own role; this one doesn't invent a new convention either.
export function AppearanceRoute() {
  const [state, setState] = useState<State>({ kind: "loading" });

  const [accentPreset, setAccentPreset] = useState<AccentPreset>("terracotta");
  const [themeMode, setThemeMode] = useState<ThemeMode>("system");
  const [fontPreset, setFontPreset] = useState<FontPreset>("sans");
  const [radiusPreset, setRadiusPreset] = useState<RadiusPreset>("default");

  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const business = await tenantApi.currentBusiness();
      setAccentPreset(business.branding.accent_preset);
      setThemeMode(business.branding.theme_mode);
      setFontPreset(business.branding.font_preset);
      setRadiusPreset(business.branding.radius_preset);
      setState({ kind: "ready" });
    } catch (err) {
      setState({
        kind: "error",
        message: describe(err, "Görünüm ayarları alınamadı."),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A change after a successful save clears the stale "kaydedildi" banner:
  // the on-screen values no longer match what was last persisted until the
  // next Kaydet. The values themselves are left exactly as the user set
  // them — only the success/error feedback resets.
  function clearFeedback() {
    setSaveSuccess(false);
    setSaveError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await tenantApi.updateBusinessBranding({
        accent_preset: accentPreset,
        theme_mode: themeMode,
        font_preset: fontPreset,
        radius_preset: radiusPreset,
      });
      setSaveSuccess(true);
    } catch (err) {
      // A bare 401 means the token itself was already invalid: the client's
      // onUnauthorized has already cleared the session and is navigating to
      // /login, so there is nothing further to show here — same guard as
      // Account.tsx's password change.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setSaveError(describe(err, "Görünüm ayarları kaydedilemedi."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Görünüm</CardTitle>
          <CardDescription>
            Menünüzün genel görünüm ayarlarını buradan yönetin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="state">Yükleniyor…</div>
        </CardContent>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Görünüm</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="state error">{state.message}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Görünüm</CardTitle>
        <CardDescription>
          Menünüzün genel görünüm ayarlarını buradan yönetin. Kaydettiğiniz
          ayarlar genel menünüze uygulanır.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="appearance-form" onSubmit={handleSubmit}>
          <fieldset className="qr-swatch-fieldset" disabled={submitting}>
            <legend className="qr-swatch-legend">Vurgu rengi</legend>
            <div
              className="qr-swatch-group"
              role="radiogroup"
              aria-label="Vurgu rengi"
            >
              {ACCENT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="qr-swatch-option"
                  data-selected={
                    accentPreset === option.value ? "true" : undefined
                  }
                >
                  <input
                    type="radio"
                    name="accent_preset"
                    value={option.value}
                    checked={accentPreset === option.value}
                    onChange={() => {
                      setAccentPreset(option.value);
                      clearFeedback();
                    }}
                  />
                  <span
                    className={`qr-swatch-circle ${option.swatchClass}`}
                    aria-hidden="true"
                  />
                  <span className="qr-swatch-label">{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <Field
            label="Varsayılan tema"
            htmlFor="appearance-theme-mode"
            description="Menü ilk açıldığında kullanılacak görünüm."
          >
            <select
              id="appearance-theme-mode"
              value={themeMode}
              onChange={(e) => {
                setThemeMode(e.target.value as ThemeMode);
                clearFeedback();
              }}
              disabled={submitting}
            >
              {THEME_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Yazı tipi" htmlFor="appearance-font-preset">
            <select
              id="appearance-font-preset"
              value={fontPreset}
              onChange={(e) => {
                setFontPreset(e.target.value as FontPreset);
                clearFeedback();
              }}
              disabled={submitting}
            >
              {FONT_PRESET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Köşe yuvarlaklığı" htmlFor="appearance-radius-preset">
            <select
              id="appearance-radius-preset"
              value={radiusPreset}
              onChange={(e) => {
                setRadiusPreset(e.target.value as RadiusPreset);
                clearFeedback();
              }}
              disabled={submitting}
            >
              {RADIUS_PRESET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          {saveError && <div className="state error">{saveError}</div>}
          {saveSuccess && (
            <div className="state ok">Görünüm ayarları kaydedildi.</div>
          )}

          <div className="login-actions">
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
